import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import AuthGuard from '../components/AuthGuard';
import CardFundPromotionModal from '../components/CardFundPromotionModal';
import CardFundPromotionModalV2 from '../components/CardFundPromotionModalV2';
import CardFundPromotionModalV3 from '../components/CardFundPromotionModalV3';
import { api } from '../lib/api';

export default function CardPasswordSetup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const [userInfo, setUserInfo] = useState<{
    name: string;
    member_number: string;
    id_number_masked: string;
    is_activated: boolean;
  } | null>(null);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [hasFundPurchase, setHasFundPurchase] = useState(false);
  const [fundPurchasedCount, setFundPurchasedCount] = useState(0);
  const [fundMaxCount, setFundMaxCount] = useState(3);
  const [showFundModal, setShowFundModal] = useState(false);
  const [showRequireFundModal, setShowRequireFundModal] = useState(false);

  useEffect(() => {
    api.get('/card-password-setup/status').then(res => {
      setUserInfo(res.data.user_info);
      setHasSubmitted(res.data.has_submitted);
      setHasFundPurchase(res.data.has_fund_purchase);
      setFundPurchasedCount(res.data.fund_purchased_count);
      setFundMaxCount(res.data.fund_max_count);
    }).catch(() => {
      toast.error('加载失败');
    }).finally(() => setLoading(false));
  }, []);

  async function handleSubmit() {
    if (submitting || hasSubmitted) return;
    if (!hasFundPurchase) {
      toast.error('需先办理一份红色初心·党员钱包基金即可设置密码');
      return;
    }
    if (!password || password.length !== 6) {
      toast.error('请输入6位数字支付密码');
      return;
    }
    if (!/^\d{6}$/.test(password)) {
      toast.error('密码必须为6位数字');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('两次输入的密码不一致');
      return;
    }
    const sequential = '0123456789';
    const reverseSeq = '9876543210';
    if (sequential.includes(password) || reverseSeq.includes(password)) {
      toast.error('请勿使用连续数字作为密码');
      return;
    }
    if (/^(\d)\1{5}$/.test(password)) {
      toast.error('请勿使用重复数字作为密码');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/card-password-setup', { password, password_confirmation: confirmPassword });
      toast.success('密码设置成功');
      setHasSubmitted(true);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || '设置失败');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-md mx-auto min-h-screen flex items-center justify-center">
        <AuthGuard />
        <div className="text-gray-400 text-sm">加载中...</div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto min-h-screen relative">
      <AuthGuard />

      {/* Background */}
      <div className="relative">
        <img src="/images/cardps_beijing.png" alt="" className="w-full" />
        {/* Content overlay */}
        <div className="absolute top-0 left-0 right-0 w-full" style={{ paddingTop: '220px' }}>
          {/* Instruction text */}
          <p className="text-[13px] text-[#5a3e2b] text-center px-6 mb-3 mt-3">
            请仔细核对以下个人信息，确认无误后设置您的支付密码：
          </p>

          {/* Info card */}
          <div className="mx-4 relative">
            <img src="/images/cardps_kuang.png" alt="" className="w-full" />
            <div className="absolute inset-0 flex items-center px-4 py-3">
              {/* Avatar */}
              <div className="flex-shrink-0 w-[80px] mr-3">
                <img src="/images/cardps_touxinag.png" alt="" className="w-full rounded-lg" />
              </div>
              {/* Info */}
              <div className="flex-1 space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <img src="/images/cardps_xing.png" alt="" className="w-[20px] h-[20px]" />
                  <span className="text-[12px] text-[#666]">姓名：</span>
                  <span className="text-[13px] font-bold text-[#333]">{userInfo?.name || '-'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <img src="/images/cardps_dnagyuan.png" alt="" className="w-[20px] h-[20px]" />
                  <span className="text-[12px] text-[#666]">党员编号：</span>
                  <span className="text-[13px] font-bold text-[#333]">{userInfo?.member_number || '-'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <img src="/images/cardps_shenfen.png" alt="" className="w-[20px] h-[20px]" />
                  <span className="text-[12px] text-[#666]">身份证号：</span>
                  <span className="text-[13px] font-bold text-[#333]">{userInfo?.id_number_masked || '-'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <img src="/images/cardps_jihuo.png" alt="" className="w-[20px] h-[20px]" />
                  <span className="text-[12px] text-[#666]">激活状态：</span>
                  <span className="text-[13px] font-bold text-green-600">
                    {userInfo?.is_activated ? '已激活' : '未激活'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Password inputs */}
          <div className="mx-4 mt-5 space-y-3">
            {/* Set password */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 flex-shrink-0 w-[105px]">
                <img src="/images/cardps_miam.png" alt="" className="w-[22px] h-[22px]" />
                <span className="text-[13px] font-bold text-[#333]">设置密码：</span>
              </div>
              <div className="flex-1 relative">
                <img src="/images/cardps_shuru.png" alt="" className="w-full" />
                <input
                  type={hasSubmitted ? 'password' : showPassword ? 'text' : 'password'}
                  placeholder="请输入6位数字支付密码"
                  value={hasSubmitted ? '******' : password}
                  onChange={e => !hasSubmitted && setPassword(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={hasSubmitted}
                  maxLength={6}
                  inputMode="numeric"
                  className="absolute inset-0 w-full h-full bg-transparent px-3 pr-10 text-[13px] text-gray-800 placeholder-gray-400 outline-none disabled:opacity-60"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                >
                  <img src="/images/cardps_yanjing.png" alt="" className="w-[20px] h-[20px] opacity-50" />
                </button>
              </div>
            </div>

            {/* Confirm password */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 flex-shrink-0 w-[105px]">
                <img src="/images/cardps_miam.png" alt="" className="w-[22px] h-[22px]" />
                <span className="text-[13px] font-bold text-[#333]">确认密码：</span>
              </div>
              <div className="flex-1 relative">
                <img src="/images/cardps_shuru.png" alt="" className="w-full" />
                <input
                  type={hasSubmitted ? 'password' : showConfirmPassword ? 'text' : 'password'}
                  placeholder="请再次输入支付密码"
                  value={hasSubmitted ? '******' : confirmPassword}
                  onChange={e => !hasSubmitted && setConfirmPassword(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={hasSubmitted}
                  maxLength={6}
                  inputMode="numeric"
                  className="absolute inset-0 w-full h-full bg-transparent px-3 pr-10 text-[13px] text-gray-800 placeholder-gray-400 outline-none disabled:opacity-60"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                >
                  <img src="/images/cardps_yanjing.png" alt="" className="w-[20px] h-[20px] opacity-50" />
                </button>
              </div>
            </div>

            {/* Warning */}
            <div className="flex items-center justify-center gap-1 mt-1">
              <img src="/images/cardps_qingwu.png" alt="" className="w-[16px] h-[16px]" />
              <span className="text-[11px] text-[#c41a1a]">请勿使用连续数字或生日等简单密码</span>
            </div>
          </div>

          {/* Important notice */}
          <div className="mx-4 mt-4">
            <img src="/images/cardps_zhongyao.png" alt="" className="w-full" />
          </div>

          {/* Submit button / Success message */}
          <div className="mx-8 mt-2 mb-2">
            {hasSubmitted ? (
              <div className="text-center space-y-3">
                <div className="bg-[#fff8e6] border border-[#f5d67b] rounded-xl px-2 py-2">
                  <p className="text-xs font-bold text-[#c41a1a]">五个工作日后开放资金使用</p>
                </div>
                {fundPurchasedCount < fundMaxCount && (
                  <div className="bg-[#fff8e6] border border-[#f5d67b] rounded-xl px-2 py-2">
                    <button
                      onClick={() => setShowFundModal(true)}
                      className="text-[13px] text-[#c41a1a] underline"
                    >
                      继续办理红色初心·党员钱包基金（{fundPurchasedCount}/{fundMaxCount}）
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => {
                  if (!hasFundPurchase) {
                    setShowRequireFundModal(true);
                  } else {
                    handleSubmit();
                  }
                }}
                disabled={submitting}
                className="w-full relative disabled:opacity-60"
              >
                <img src="/images/cardps_queren.png" alt="" className="w-full" />
                <span className="absolute inset-0 flex items-center justify-center text-[#fef0c8] text-[18px] font-bold"
                  style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
                >
                  {submitting ? '设置中...' : '确认设置并激活'}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Require Fund Modal */}
      {showRequireFundModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowRequireFundModal(false)}>
          <div className="bg-white rounded-2xl w-[85%] max-w-sm p-6 text-center" onClick={e => e.stopPropagation()}>
            <div className="text-[40px] mb-2">⚠️</div>
            <p className="text-[15px] font-bold text-[#333] mb-4">需先办理一份红色初心·党员钱包基金即可设置密码</p>
            <button
              onClick={() => { setShowRequireFundModal(false); setShowFundModal(true); }}
              className="w-full py-2.5 rounded-xl text-base font-bold text-white"
              style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}
            >
              立即办理
            </button>
            <button
              onClick={() => setShowRequireFundModal(false)}
              className="w-full mt-2 py-2 text-sm text-gray-400"
            >
              取消
            </button>
          </div>
        </div>
      )}

      <CardFundPromotionModal visible={showFundModal} onClose={() => setShowFundModal(false)} />
      <CardFundPromotionModalV2 visible={showFundModal} onClose={() => setShowFundModal(false)} />
      <CardFundPromotionModalV3 visible={showFundModal} onClose={() => setShowFundModal(false)} />
    </div>
  );
}
